import { useClient } from '@cortex/react-query';
import type { NodeExecution } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties } from 'react';

import { formatDateTime, formatDuration, formatUsd } from '../../lib/format';
import { isManagedRunRedaction } from '../../lib/managed-run';
import { EmptyState } from '../common/EmptyState';
import { Spinner } from '../common/Spinner';
import { useWorkflowEditorOptional } from '../workflows/WorkflowEditorContext';

import { ManagedRunRedactedNote } from './ManagedRunRedactedNote';
import { RunMessageThread } from './RunMessageThread';

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

type Status = NodeExecution['status'];

const TERMINAL: ReadonlySet<Status> = new Set(['COMPLETED', 'FAILED', 'SKIPPED']);

const card: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius)',
  overflow: 'hidden',
};

const stepRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--sp-3)',
  padding: 'var(--sp-3)',
  background: 'transparent',
  border: 'none',
  width: '100%',
  cursor: 'pointer',
  color: 'inherit',
  textAlign: 'left' as const,
};

const stepDot: CSSProperties = {
  flex: '0 0 auto',
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  marginTop: 2,
};

const stepBody: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const stepHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const stepMeta: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-3)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
};

const stepSummary: CSSProperties = {
  // Backend-derived single-line summary of what the step produced —
  // assistant's last message for LLM nodes, output.text for everything
  // else (DEV-16173 extensions). Clamped server-side; we ellipsis here
  // too in case the row's narrower than 200 chars worth of text.
  fontSize: 'var(--fs-sm)',
  color: 'var(--text-primary)',
  fontStyle: 'italic',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginTop: 2,
};

const progressBar: CSSProperties = {
  width: '100%',
  height: 4,
  background: 'var(--surface-2)',
  borderRadius: 2,
  overflow: 'hidden',
  marginTop: 'var(--sp-2)',
};

const progressFill: CSSProperties = {
  height: '100%',
  background: 'var(--horizon-glow)',
  transition: 'width 240ms ease',
};

/**
 * Human-readable execution view for a single run. Replaces the previous
 * raw-JSON output dump with a list of expandable steps — one per node
 * execution — that surface inputs / outputs / errors on demand.
 * (DEV-16124.)
 *
 * Driven by `/v1/runs/:id/node-executions` (the richer endpoint; the
 * older `/trace` carried only metadata). Polls every 2.5s while the
 * run isn't terminal so the active step indicator stays current
 * without depending on the SSE channel.
 */
export function RunExecutionTrace({ runId, isActive }: { runId: string; isActive: boolean }) {
  const client = useClient();
  const executions = useQuery({
    queryKey: ['run-node-executions', runId, 'trace'],
    queryFn: () =>
      client.http
        .get(`v1/runs/${runId}/node-executions?limit=200`)
        .json<IListEnvelope<NodeExecution> | NodeExecution[]>(),
    refetchInterval: isActive ? 2500 : false,
  });
  // Clear the sidebar → canvas focus when the run-detail surface
  // closes (panel dismissed, route navigated away). Pairs with the
  // per-step set/clear inside `StepItem`. Safe to call on the
  // standalone triage page since the optional hook returns null.
  // (DEV-16197.)
  const editor = useWorkflowEditorOptional();
  useEffect(() => {
    return () => {
      editor?.setFocusedNodeKey(null);
    };
  }, [editor]);

  if (executions.isLoading) return <Spinner />;
  if (executions.error) {
    return (
      <div className="alert alert--danger">
        Failed to load trace: {(executions.error as Error).message}
      </div>
    );
  }

  const raw = executions.data;
  const steps = sortSteps(Array.isArray(raw) ? raw : (raw?.items ?? []));

  if (steps.length === 0) {
    return (
      <EmptyState
        title="No steps yet"
        description={
          isActive
            ? 'The runner hasn’t emitted a node execution yet. Steps will appear here as the workflow runs.'
            : 'This run finished without recording any node executions.'
        }
      />
    );
  }

  const { completed, total } = stepCounts(steps);

  return (
    <div style={card}>
      {isActive ? (
        <div
          style={{
            padding: 'var(--sp-3)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-secondary)',
            }}
          >
            <span>Progress</span>
            <span className="t-mono">
              {completed} / {total}
            </span>
          </div>
          <div style={progressBar}>
            <div
              style={{ ...progressFill, width: `${total === 0 ? 0 : (completed / total) * 100}%` }}
              aria-hidden="true"
            />
          </div>
        </div>
      ) : null}
      <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {steps.map((ex) => (
          <StepItem key={ex.id} execution={ex} />
        ))}
      </ol>
    </div>
  );
}

function StepItem({ execution: ex }: { execution: NodeExecution }) {
  const [expanded, setExpanded] = useState(false);
  const status = ex.status;
  const summary = readTraceSummary(ex);
  // Sidebar ↔ canvas correspondence (DEV-16197). When the user expands
  // a step, we mark the matching canvas node as `is-sidebar-focused`
  // via the editor context. The standalone /triage/runs/:id page has
  // no editor context — `useWorkflowEditorOptional` returns null and
  // the highlight call below short-circuits.
  //
  // Requires the joined `ex.nodeKey` from DEV-16196's API change to
  // resolve the canvas node id. If `nodeKey` isn't set (legacy rows
  // whose WorkflowNode was deleted), the call is a no-op — better
  // than highlighting the wrong node.
  const editor = useWorkflowEditorOptional();
  // `nodeKey` comes from DEV-16196's API join + schema additions
  // (PR #292). Type-asserted here so this PR compiles against `main`
  // regardless of merge order — once #292 lands the assertion becomes
  // redundant but still valid. If the API hasn't shipped that field
  // yet, the value is undefined and the focus call no-ops below.
  const focusableKey = (ex as NodeExecution & { nodeKey?: string | null }).nodeKey ?? null;
  return (
    <li style={{ borderBottom: '1px solid var(--border-subtle)' }}>
      <button
        type="button"
        style={stepRow}
        onClick={() => {
          setExpanded((wasExpanded) => {
            const willBeExpanded = !wasExpanded;
            if (editor && focusableKey) {
              // Set focus when expanding; clear when collapsing (so a
              // collapse always tidies up after itself). If another
              // step is then expanded, its click handler sets focus to
              // its own key — single-focus semantics fall out of this
              // for free.
              editor.setFocusedNodeKey(willBeExpanded ? focusableKey : null);
            }
            return willBeExpanded;
          });
        }}
        aria-expanded={expanded}
      >
        <span style={{ ...stepDot, ...stepDotStyle(status) }} aria-hidden="true">
          {stepDotGlyph(status)}
        </span>
        <span style={stepBody}>
          <span style={stepHeader}>
            <strong className="t-mono">{ex.nodeKey ?? `${ex.nodeId.slice(0, 8)}…`}</strong>
            <span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
              <span className={`badge ${badgeClassFor(status)}`}>{status}</span>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
                {expanded ? '▾' : '▸'}
              </span>
            </span>
          </span>
          {summary ? (
            <span style={stepSummary} title={summary}>
              {summary}
            </span>
          ) : null}
          <span style={stepMeta}>
            <span>{formatDateTime(ex.startedAt)}</span>
            <span>· {formatDuration(ex.startedAt, ex.finishedAt)}</span>
            {typeof ex.costUsd === 'number' ? <span>· {formatUsd(ex.costUsd)}</span> : null}
            {ex.tokensIn || ex.tokensOut ? (
              <span>
                · tok {ex.tokensIn}/{ex.tokensOut}
              </span>
            ) : null}
          </span>
        </span>
      </button>
      {expanded ? <StepDetails execution={ex} /> : null}
    </li>
  );
}

/**
 * Read the executor-derived one-line summary from `trace.summary` (DEV-16173
 * extensions). Falls back to `null` for nodes whose trace blob doesn't
 * carry it — the step header just skips rendering the summary line in
 * that case rather than showing "Completed" / empty filler.
 */
function readTraceSummary(ex: NodeExecution): string | null {
  const trace = ex.trace;
  if (!trace || typeof trace !== 'object') return null;
  const value = (trace as Record<string, unknown>).summary;
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * Expanded per-step content. Renders a message-thread layout via
 * `RunMessageThread` — bubbles, markdown bodies, role-aware coloring —
 * matching triage's `TaskNode` aesthetic. The legacy three-`<pre>JSON…</pre>`
 * sections were replaced in DEV-16173.
 */
function StepDetails({ execution: ex }: { execution: NodeExecution }) {
  // A managed-workflow run's payloads come back as a placeholder for callers
  // who may not inspect them; status, timing and cost above stay visible.
  if (
    isManagedRunRedaction(ex.trace) ||
    isManagedRunRedaction(ex.input) ||
    isManagedRunRedaction(ex.output)
  ) {
    return <ManagedRunRedactedNote />;
  }
  return <RunMessageThread execution={ex} />;
}

function stepDotStyle(status: Status): CSSProperties {
  if (status === 'COMPLETED')
    return { background: 'rgba(120, 180, 140, 0.85)', color: 'var(--bg, #000)' };
  if (status === 'FAILED') return { background: 'var(--status-danger)', color: 'var(--bg, #000)' };
  if (status === 'RUNNING')
    return {
      background: 'transparent',
      color: 'var(--terminal-amber)',
      border: '2px solid var(--terminal-amber)',
      boxShadow: '0 0 0 1px var(--terminal-amber), 0 0 12px -2px var(--terminal-amber)',
    };
  if (status === 'PAUSED')
    return {
      background: 'transparent',
      color: 'var(--terminal-amber)',
      border: '2px dashed var(--terminal-amber)',
    };
  if (status === 'SKIPPED') return { background: 'var(--text-muted)', color: 'var(--bg, #000)' };
  return {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '2px solid var(--border-subtle)',
  };
}

function stepDotGlyph(status: Status): string {
  if (status === 'COMPLETED') return '✓';
  if (status === 'FAILED') return '✕';
  if (status === 'RUNNING') return '●';
  if (status === 'PAUSED') return '⏸';
  if (status === 'SKIPPED') return '↷';
  return '○';
}

function badgeClassFor(status: Status): string {
  if (status === 'COMPLETED') return 'badge--success';
  if (status === 'FAILED') return 'badge--danger';
  if (status === 'RUNNING') return 'badge--warning';
  if (status === 'PAUSED') return 'badge--warning';
  if (status === 'SKIPPED') return 'badge--neutral';
  return 'badge--neutral';
}

/**
 * Sort node executions in execution order. `startedAt` is the
 * authoritative key; ties (or missing values, for PENDING / queued
 * nodes) fall back to `createdAt` so the list never collapses to a
 * random order during the brief window before a step actually starts.
 * Exported for unit testing.
 */
export function sortSteps(executions: readonly NodeExecution[]): NodeExecution[] {
  return [...executions].sort((a, b) => {
    const aT = a.startedAt ? new Date(a.startedAt).getTime() : 0;
    const bT = b.startedAt ? new Date(b.startedAt).getTime() : 0;
    if (aT !== bT) return aT - bT;
    const aC = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bC = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return aC - bC;
  });
}

/**
 * Count terminal vs total steps for the progress indicator. Anything
 * not in {COMPLETED, FAILED, SKIPPED} counts as in-flight regardless
 * of whether it's actually running yet (PENDING + PAUSED + RUNNING).
 * Exported for unit testing.
 */
export function stepCounts(executions: readonly NodeExecution[]): {
  completed: number;
  total: number;
} {
  let completed = 0;
  for (const ex of executions) {
    if (TERMINAL.has(ex.status)) completed += 1;
  }
  return { completed, total: executions.length };
}
